import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { filterBiomarkers, type Biomarker } from '@/lib/biomarkers';
import { DomainPill } from '@/components/DomainPill';
import { AdminPage, RegistryTable, FeedbackControl } from '@/admin';

export function BiomarkersPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['biomarkers'], queryFn: () => adminApi.biomarkers.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', 'biomarkers'], queryFn: () => feedbackApi.summary('biomarkers') });
  const [search, setSearch] = useState('');

  const all = q.data?.biomarkers ?? [];
  const rows = filterBiomarkers(all, search);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', 'biomarkers'] });

  return (
    <AdminPage title="Biomarker Registry" description="Every canonical measured value, its domain, data source, and aliases.">
      {q.data ? <div className="mb-13 text-body-sm text-text-secondary">{q.data.biomarker_count} biomarkers · {q.data.alias_count} aliases</div> : null}
      <RegistryTable<Biomarker>
        columns={[
          { key: 'name', header: 'Biomarker', render: (b) => (
            <Link to={`/admin/biomarkers/${encodeURIComponent(b.name)}`} className="font-mono text-[12px] text-accent hover:underline">{b.name}</Link>
          ) },
          { key: 'domain', header: 'Domain', render: (b) => b.primary_domain ? <DomainPill domain={b.primary_domain} /> : <span className="text-text-muted text-[12px]">—</span> },
          { key: 'source', header: 'Source', className: 'text-[12px] text-text-secondary', render: (b) => b.data_source || '—' },
          { key: 'aliases', header: 'Aliases', className: 'font-mono text-[12px]', render: (b) => (b.aliases ?? []).length },
        ]}
        rows={rows}
        getRowKey={(b) => b.name}
        count={all.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={search ? `No biomarkers match "${search}"` : 'No biomarkers.'}
        search={{ value: search, onChange: setSearch, placeholder: 'Search by name, domain, or alias…' }}
        rowActions={(b) => <FeedbackControl registry="biomarkers" itemId={b.name} itemLabel={b.name} openCount={fb.data?.[b.name]?.open ?? 0} onChanged={refresh} />}
      />
    </AdminPage>
  );
}
